import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { apiService } from './services/apiService';
import AppShell from './components/layout/AppShell';
import LoginPage from './components/LoginPage';
import PipelineHub from './components/pages/PipelineHub';
import ContentDesk from './components/pages/ContentDesk';
import ArticleInspector from './components/pages/ArticleInspector';
import AgentFlow from './components/pages/AgentFlow';
import SystemConfig from './components/pages/SystemConfig';
import UrlToArticle from './components/pages/UrlToArticle';
import NewsDiscovery from './components/pages/NewsDiscovery';
import FeedbackDesk from './components/pages/FeedbackDesk';
import HelpPageRoute from './components/pages/HelpPageRoute';
import SourcesPage from './components/pages/SourcesPage';
import LogsPage from './components/pages/LogsPage';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, login } = useAuth();
  const [backendReady, setBackendReady] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkBackend = async () => {
      const isHealthy = await apiService.checkHealth();
      setBackendReady(isHealthy);
      setLoading(false);
    };
    checkBackend();
    const interval = setInterval(checkBackend, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: '#f5f6fa' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4"
            style={{ borderColor: 'rgba(79,70,229,0.2)', borderTopColor: '#4f46e5' }} />
          <p className="font-body text-body-sm" style={{ color: '#94a3b8' }}>Connecting to backend...</p>
        </div>
      </div>
    );
  }

  if (!backendReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 px-6">
        <div className="bg-white rounded-2xl p-8 max-w-lg text-center shadow-elevated border border-slate-200">
          <span className="material-symbols-outlined text-[48px] text-red-400 mb-4 block">cloud_off</span>
          <h2 className="font-display text-headline-md text-slate-900 mb-2">Backend Not Available</h2>
          <p className="text-slate-500 font-body text-body-md mb-4">
            The backend server is not running. Start the Python backend on port 8000.
          </p>
          <code className="block p-3 rounded-lg font-mono text-label-sm" style={{ background: '#f1f5f9', color: '#4f46e5' }}>
            python main.py
          </code>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/pipeline" replace /> : <LoginPage onLogin={login} />
        } />
        <Route element={<RequireAuth><AppShell /></RequireAuth>}>
          <Route index element={<Navigate to="/pipeline" replace />} />
          <Route path="pipeline" element={<PipelineHub />} />
          <Route path="content" element={<ContentDesk />} />
          <Route path="article/:articleId" element={<ArticleInspector />} />
          <Route path="agents" element={<AgentFlow />} />
          <Route path="discover" element={<NewsDiscovery />} />
          <Route path="url-to-article" element={<UrlToArticle />} />
          <Route path="feedback" element={<FeedbackDesk />} />
          <Route path="system" element={<SystemConfig />} />
          <Route path="help" element={<HelpPageRoute />} />
          <Route path="sources" element={<SourcesPage />} />
          <Route path="logs" element={<LogsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/pipeline" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
